import { omit } from 'lodash';
import React, { FC, FunctionComponent, InputHTMLAttributes, Suspense, useState } from 'react';
import { graphql, useLazyLoadQuery } from 'react-relay/hooks';
import { OptionProps, components } from 'react-select';
import { BadgeGray } from 'src/components/widgets/badge';
import Loader from 'src/components/widgets/loader';
import ValidationMessage from 'src/components/widgets/validation/validationMessage';
import MdOpenWith from 'src/shared/icons/arrows-alt-solid.svg';
import MdArrowDropDown from 'src/shared/icons/caret-down-solid.svg';
import MdArrowDropUp from 'src/shared/icons/caret-up-solid.svg';
import MdOutlineDone from 'src/shared/icons/check-solid.svg';
import { default as MdInfo } from 'src/shared/icons/info-circle-solid.svg';
import EditButtonPen from 'src/shared/icons/pen-solid.svg';
import MdAddCircleOutline from 'src/shared/icons/plus-circle-solid.svg';
import PlusIcon from 'src/shared/icons/plus-solid.svg';
import TrashIcon from 'src/shared/icons/trash-alt.svg';
import styled, { css } from 'styled-components';
import { BasicButton, Button, Link, LinkButton } from '../components/widgets/buttons';
import { Icon } from '../components/widgets/icon';
import { Input, PasswordInput } from '../components/widgets/input';
import { AiSparklesTag, BaseCardTag, ComingSoonTag, CrownBadgeTag, EarlyAccessTag, NewTag } from '../components/widgets/tag';
import { ZTooltip } from '../components/widgets/ztooltip';
import type { ZakekeEnvironment, globalsOnlyNewUIQuery } from './__generated__/globalsOnlyNewUIQuery.graphql';
import { useAppState } from './appState';
import { ReactType, T, isMobile } from './helpers';
import ExclamationCircleSolid from './svg/exclamationCircleSolid';
import { useValidation } from './validation';
export interface TemplatePlaceholder {
    value: string;
    description: string;
    iterable?: boolean;
    complex?: boolean;
    children?: TemplatePlaceholder[];
}

type ButtonSizeType = 'small' | 'medium' | 'large' | 'fullWidth';
export const scrollbarsStyle = css`
    *::-webkit-scrollbar {
        width: 3px;
    }

    *::-webkit-scrollbar-track {
        background: ${(props) => props.theme.app.backgroundColor};
        border-radius: 20px;
    }

    *::-webkit-scrollbar-thumb {
        background: ${(props) => props.theme.app.primary_light};
        border-radius: 20px;
    }

    scrollbar-color: ${(props) => props.theme.app.primary_light} ${(props) => props.theme.app.backgroundColor};
    scrollbar-width: thin;
`;

// #region Helpers

export const truncate = () => `
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  min-width: 0;
`;

export const hoverOpacity = () => `
  transition: opacity ease 0.2s;
  cursor: pointer;

  &:hover {
    opacity: 0.5;
  }
`;

export const hoverBorderShadow = () => css`
    transition: filter ease 0.2s;
    cursor: pointer;

    &:hover {
        filter: drop-shadow(0px 0px 3px rgba(0, 0, 0, 0.1)) drop-shadow(0px 4px 20px rgba(0, 0, 0, 0.15));
    }
`;

export const tint = (hex: string, amount: number) => {
    let R = parseInt(hex.substring(1, 3), 16);
    let G = parseInt(hex.substring(3, 5), 16);
    let B = parseInt(hex.substring(5, 7), 16);

    const getSingle = (number: number) => Math.round((number * (100 + amount)) / 100);

    R = getSingle(R);
    G = getSingle(G);
    B = getSingle(B);

    R = R < 255 ? R : 255;
    G = G < 255 ? G : 255;
    B = B < 255 ? B : 255;

    const getDouble = (number: number) => (number.toString(16).length === 1 ? `0${number.toString(16)}` : number.toString(16));

    const RR = getDouble(R);
    const GG = getDouble(G);
    const BB = getDouble(B);

    return `#${RR}${GG}${BB}`;
};

const buttonStyle = css<{ disabled?: boolean }>`
    font-family: ${(props) => props.theme.app.font};
    font-size: 14px;
    font-weight: 700;
    color: ${(props) => props.theme.app.component.button.primary.default.textColor};
    border: none;
    border-radius: ${(props) => props.theme.app.component.button.borderRadius};
    padding: 10px 32px;
    align-items: center;
    justify-content: center;
    transition: background-color ease 0.2s;
    position: relative;
    background-color: ${(props) =>
        !props.disabled ? props.theme.app.component.button.primary.default.backgroundColor : props.theme.app.component.button.disabled.backgroundColor};
    text-decoration: none;
    &:hover:not([disabled]) {
        cursor: pointer;
        color: ${(props) => props.theme.app.component.button.primary.default.textColor};
        background-color: ${(props) => tint(props.theme.app.component.button.primary.default.backgroundColor, 16)};
    }
`;

export const SmallColContainer = styled.div`
    display: flex;
    justify-content: flex-start;
    width: 100%;
`;

export const ColorPaletteGroupButtons = styled.div`
    display: flex;
    justify-content: flex-end;
    align-items: center;
    a {
        color: ${(props) => props.theme.app.primary};
    }
`;
export const ColorPickerAndInputWrapper = styled.div`
    position: relative;
`;

export const DestructiveButton = styled(Button)`
    background-color: ${(props) => props.theme.app.component.button.destructive.default.backgroundColor};
    border: ${(props) => props.theme.app.component.button.destructive.border};
    color: ${(props) => props.theme.app.component.button.destructive.default.textColor};

    &[disabled] {
        opacity: ${(props) => props.theme.app.component.button.destructive.disabled.opacity};
        background-color: ${(props) => props.theme.app.component.button.destructive.disabled.backgroundColor};
        color: ${(props) => props.theme.app.component.button.destructive.disabled.textColor};
        border: ${(props) => props.theme.app.component.button.destructive.disabled.border};
    }

    &:hover:not([disabled]) {
        background-color: ${(props) => props.theme.app.component.button.destructive.hover.backgroundColor};
        color: ${(props) => props.theme.app.component.button.destructive.hover.textColor};
    }

    &:active {
        background-color: ${(props) => props.theme.app.component.button.destructive.active.backgroundColor};
        color: ${(props) => props.theme.app.component.button.destructive.active.textColor};
    }
`;

export const BasicBetaButton = styled(Button)`
    background-color: ${(props) => props.theme.app.component.button.basic.default.backgroundColor};
    border: ${(props) => props.theme.app.component.button.basic.border};
    color: ${(props) => props.theme.app.component.button.basic.default.textColor};

    &[disabled] {
        opacity: ${(props) => props.theme.app.component.button.basic.disabled.opacity};
        background-color: ${(props) => props.theme.app.component.button.basic.disabled.backgroundColor};
        color: ${(props) => props.theme.app.component.button.basic.disabled.textColor};
        border: ${(props) => props.theme.app.component.button.basic.disabled.border};
    }

    &:hover:not([disabled]) {
        background-color: ${(props) => props.theme.app.component.button.basic.hover.backgroundColor};
        color: ${(props) => props.theme.app.component.button.basic.hover.textColor};
    }

    &:active {
        background-color: ${(props) => props.theme.app.component.button.basic.active.backgroundColor};
        color: ${(props) => props.theme.app.component.button.basic.active.textColor};
    }

    &:after {
        content: 'Beta';
        position: absolute;
        top: 7px;
        font-size: 12px;
        line-height: 15px;
        left: -13px;
        height: 0px;
        border-bottom: 15px solid gold;
        border-right: 15px solid transparent;
        border-left: 15px solid transparent;
        transform: rotate(-45deg);
    }
`;

// Must be unified with BasicButton
// https://www.wrike.com/open.htm?id=1096711490
export const LinkBasicButton = styled(LinkButton)`
    background-color: ${(props) => props.theme.app.component.button.basic.default.backgroundColor};
    border: ${(props) => props.theme.app.component.button.basic.border};
    color: ${(props) => props.theme.app.component.button.basic.default.textColor};

    &[disabled] {
        opacity: ${(props) => props.theme.app.component.button.basic.disabled.opacity};
        background-color: ${(props) => props.theme.app.component.button.basic.disabled.backgroundColor};
        color: ${(props) => props.theme.app.component.button.basic.disabled.textColor};
        border: ${(props) => props.theme.app.component.button.basic.disabled.border};
    }

    &:hover:not([disabled]) {
        background-color: ${(props) => props.theme.app.component.button.basic.hover.backgroundColor};
        color: ${(props) => props.theme.app.component.button.basic.hover.textColor};
    }

    &:active {
        background-color: ${(props) => props.theme.app.component.button.basic.active.backgroundColor};
        color: ${(props) => props.theme.app.component.button.basic.active.textColor};
    }
`;
interface DropdownButtonItem {
    label: string;
    onClick: () => void;
}

const DropdownButtonContainer = styled.div`
    display: inline-block;
    position: relative;
`;

const DropdownButtonMenuContainer = styled.div`
    position: absolute;
    top: 100%;
    left: 0;
    width: 100%;
    z-index: 2;

    ${Button} {
        margin-top: 10px;
    }
`;

export const DropdownButton: FC<React.ComponentProps<'button'> & { dropdownItems: DropdownButtonItem[] }> = (props) => {
    const [isOpened, setOpened] = useState(false);

    return (
        <DropdownButtonContainer>
            <Button {...omit(props, ['ref', 'dropdownItems'])} onClick={() => setOpened(!isOpened)}>
                {props.children}
            </Button>

            {isOpened && (
                <DropdownButtonMenuContainer>
                    {props.dropdownItems.map((x, idx) => {
                        return (
                            <BasicButton key={idx} onClick={x.onClick}>
                                {x.label}
                            </BasicButton>
                        );
                    })}
                </DropdownButtonMenuContainer>
            )}
        </DropdownButtonContainer>
    );
};

// #endregion

export const EmptyMessage = styled.div`
    font-size: 20px;
    font-weight: bold;
    color: #bbb;
    text-align: center;
    margin: 40px 0;
`;

export const TitleMessageContainer = styled.div`
    font-size: 18px;
    text-align: center;
    font-weight: bold;
    color: ${(props) => props.theme.app.primary};
    margin: 25px 0 5px 0;
`;

export const TextMessageContainer = styled.div`
    font-size: 18px;
    text-align: center;
    color: ${(props) => props.theme.app.primary};
    margin: 5px 0 25px 0;
`;

export const MessageContainer = styled.div`
    background-color: ${(props) => props.theme.app.component.message.backgroundColor};
    padding: ${(props) => props.theme.app.component.message.padding};
    border-radius: ${(props) => props.theme.app.component.message.borderRadius};
    font-weight: ${(props) => props.theme.app.component.message.fontWeight};
    font-size: ${(props) => props.theme.app.component.message.fontSize};

    &:not(:last-child) {
        margin-bottom: 20px;
    }
`;

export const WarningMessage = styled(MessageContainer)`
    background-color: ${(props) => props.theme.app.component.message.warning.backgroundColor};
    color: ${(props) => props.theme.app.component.message.warning.backgroundColor};
`;

export const ErrorMessage = styled(MessageContainer)`
    background-color: ${(props) => props.theme.app.component.message.error.backgroundColor};
    color: ${(props) => props.theme.app.component.message.error.color};
    font-size: ${(props) => props.theme.app.component.message.error.fontSize};

    ${Link} {
        color: ${(props) => props.theme.app.component.message.error.color};
    }
`;

export const SuccessMessageContainer = styled(MessageContainer)`
    background-color: #d6f3e2;
`;

const StyledInfoIcon = styled(MdInfo)`
    color: #a64f21;
    font-size: 20px;
    margin-right: 10px;
    width: 24px;
    height: 24px;
    vertical-align: middle;
`;

const StyledSuccessIcon = styled(MdOutlineDone)`
    font-size: 20px;
    margin-right: 10px;
    width: 24px;
    height: 24px;
`;

export const SuccessMessage: FC<{ children?: React.ReactNode }> = ({ children }) => {
    return (
        <SuccessMessageContainer>
            <StyledSuccessIcon />
            {children}
        </SuccessMessageContainer>
    );
};

export const Message: FC<{ children?: React.ReactNode }> = ({ children }) => {
    return (
        <MessageContainer>
            <StyledInfoIcon />
            {children}
        </MessageContainer>
    );
};

export const AddFirstTimeContent = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    margin: auto;
    font-size: 18px;

    span {
        margin-bottom: 20px;
    }

    ${Button} {
        min-width: 300px;
    }
`;

export interface IconProps {
    tooltip?: React.ReactElement<any> | string;
    className?: string;
    $hoverable?: boolean;
    disabled?: boolean;
    children?: React.ReactNode;
    onClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
}

const _InfoIcon = styled(Icon)`
    color: ${(props) => props.theme.app.component.icon.info.default.color};
    width: ${(props) => props.theme.app.component.icon.info.default.width};
    height: ${(props) => props.theme.app.component.icon.info.default.height};

    &:hover {
        color: ${(props) => props.theme.app.component.icon.info.hover.color};
        width: ${(props) => props.theme.app.component.icon.info.hover.width};
        height: ${(props) => props.theme.app.component.icon.info.hover.height};
    }
`;

interface InputProps {
    fullWidth?: boolean;
    isError?: boolean;
}

interface InputWithValidationProps extends InputProps, ReactType<HTMLInputElement> {
    validationKey: string;
    validate: (value: string) => boolean;
    message?: string;
    simplified?: boolean;
    deferErrors?: boolean;
}

export const InputWithValidation: FC<InputWithValidationProps> = React.forwardRef<HTMLInputElement, InputWithValidationProps>((props, ref) => {
    const { validationKey, validate, message = '', deferErrors, ...inputProps } = props;

    const validateFormulaOperator = useValidation([
        {
            key: validationKey,
            validate: validate,
            message: message
        }
    ]);

    const hasError = validate(inputProps.value?.toString() ?? '');
    validateFormulaOperator.check(inputProps.value?.toString() ?? '');

    return (
        <>
            <Input {...(inputProps as InputProps)} ref={ref} isError={deferErrors ? false : hasError} />
            <ValidationMessage validationKey={validationKey} />
        </>
    );
});

interface InputWithAddRemoveProps extends InputWithValidationProps, ReactType<HTMLInputElement> {
    removeCallback?: () => void;
    addCallback?: () => void;
    showAdd?: boolean;
    showDelete?: boolean;
}

const InputWithAddRemoveContainer = styled.div`
    position: relative;

    input {
        padding: 5px 75px 5px 10px;
    }

    a {
        position: absolute;
        top: 10px;
        right: 10px;

        &:last-child {
            right: 40px;
        }

        ${Icon} {
            color: ${(props) => props.theme.app.primary};
            svg {
                height: 20px;
                width: 20px;
            }
        }
    }
`;

export const InputWithAddRemove: FC<InputWithAddRemoveProps> = (props) => {
    const { ...inputWithValidationProps } = props;

    return (
        <>
            <InputWithAddRemoveContainer>
                <InputWithValidation {...(inputWithValidationProps as InputWithValidationProps)} />
                {props.showAdd !== false && (
                    <Link
                        onClick={() => {
                            if (props.addCallback) props.addCallback();
                        }}
                    >
                        <Icon>
                            <PlusIcon />
                        </Icon>
                    </Link>
                )}
                {props.showDelete !== false && (
                    <Link
                        onClick={() => {
                            if (props.removeCallback) props.removeCallback();
                        }}
                    >
                        <Icon>
                            <TrashIcon />
                        </Icon>
                    </Link>
                )}
            </InputWithAddRemoveContainer>
        </>
    );
};

const ProgressBarContainer = styled.div`
    height: 4px;
    background-color: #ccc;
`;

const ProgressBarContent = styled.div`
    height: 4px;
    background-color: ${(props) => props.theme.app.primary};
`;

export const ProgressBar: FC<{ progress: number }> = ({ progress }) => {
    return (
        <ProgressBarContainer>
            <ProgressBarContent style={{ width: progress + '%' }} />
        </ProgressBarContainer>
    );
};

// Used for draft purpose
export const EmptyGrayBlock: FC<{}> = styled.div`
    background-color: #ddd;
`;

export const LevelItem = styled.div<{ $fullWidth?: boolean }>`
    display: inline-flex;
    margin-right: 10px;
    ${(props) => props.$fullWidth && `flex: 1;`}
`;

interface FormButtonsProps {
    align?: 'right' | 'center' | 'left';
}

export const FormButtons = styled.div<FormButtonsProps>`
    display: flex;
    justify-content: ${(props) => (props.align == 'right' ? 'flex-end' : props.align == 'center' ? 'center' : 'flex-start')};
`;

export const Center = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
`;

export const CenterColumn = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
`;

export const CancelButton = styled(Button)`
    background-color: transparent;
    border: solid 2px;
    color: ${(props) => props.theme.button.color};
    border-color: ${(props) => props.theme.button.color};
`;

export const AddButton: FunctionComponent<{ disabled?: boolean; onClick: () => void }> = (props) => {
    return (
        <Icon $hoverable disabled={props.disabled} onClick={props.onClick}>
            <MdAddCircleOutline />
        </Icon>
    );
};

export const EditButton: FunctionComponent<{ disabled?: boolean; onClick: () => void }> = (props) => {
    return (
        <Icon $hoverable disabled={props.disabled} onClick={props.onClick}>
            <EditButtonPen />
        </Icon>
    );
};

export const DeleteButton: FunctionComponent<{ disabled?: boolean; onClick: () => void; title?: string }> = (props) => {
    return (
        <Icon $hoverable disabled={props.disabled} onClick={props.onClick}>
            <TrashIcon />
        </Icon>
    );
};

export const MoveButton: FunctionComponent<{
    collapse?: boolean;
    onClick?: () => void;
    onMoveUp?: () => void;
    onMoveDown?: () => void;
}> = (props) => {
    const [collapsed, setCollapsed] = useState(props.collapse ? !props.collapse : false);

    const handleClickUp = () => {
        if (props.collapse) setCollapsed(true);
        if (props.onMoveUp) props.onMoveUp();
    };

    const handleClickDown = () => {
        if (props.collapse) setCollapsed(true);
        if (props.onMoveDown) props.onMoveDown();
    };

    const handleMouseOver = () => {
        handleClick();
    };

    const handleClick = () => {
        setCollapsed(false);
        setTimeout(() => {
            setCollapsed(true);
        }, 2000);
    };

    return (
        <React.Fragment>
            {collapsed && (
                <Link onMouseOver={handleMouseOver}>
                    <Icon $hoverable>
                        <MdOpenWith />
                    </Icon>
                </Link>
            )}
            {!collapsed && (
                <React.Fragment>
                    {props.onMoveUp && (
                        <Link onClick={handleClickUp}>
                            <Icon $hoverable>
                                <MdArrowDropUp />
                            </Icon>
                        </Link>
                    )}
                    {props.onMoveDown && (
                        <Link onClick={handleClickDown}>
                            <Icon $hoverable>
                                <MdArrowDropDown />
                            </Icon>
                        </Link>
                    )}
                </React.Fragment>
            )}
        </React.Fragment>
    );
};

interface DividerProps {
    $height?: string;
    $isHidden?: boolean;
}

export const VerticalDivider = styled.span<DividerProps>`
    border-left: 1px dotted grey;
    background: transparent;
    margin: 0px 8px;
    width: 1px;
    ${(props) => (props.$height ? 'height: ' + props.$height + ';' : 'height: 100%;')}
    right: 250px;
    top: 10px;
    ${(props) => (props.$isHidden ? 'visibility: hidden;' : 'visibility: visible;')}
`;

export const HorizontalDivider = styled.div<{ margin?: string }>`
    border-top: 1px dotted grey;
    background: transparent;
    ${(props) => (props.margin ? 'margin: ' + props.margin + ';' : 'margin: 30px 0px;')}
    width: 100%;
    height: 1px;
    right: 250px;
    top: 10px;
`;

export const ImageContainer = styled.div<{ $isDisabled?: boolean }>`
    background-color: ${isMobile() ? '#ffffff' : '#f2f2f2'};
    padding: ${isMobile() ? '0' : '10px'};
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    margin-bottom: 10px;
    aspect-ratio: 1 / 1;

    img {
        cursor: ${(props) => (props.$isDisabled ? 'not-allowed !important' : 'pointer')};
        ${hoverOpacity}
    }

    > * {
        aspect-ratio: 1 / 1;
        width: 100%;
        object-fit: contain;
    }
`;

export const Guides = styled.ul`
    list-style: none;
    margin: 0;
    padding: 0;

    li {
        margin-bottom: 10px;
        display: flex;
        align-items: center;

        ${Icon} {
            width: 18px;
            height: 18px;
        }
    }
`;

export const Separator = styled.hr`
    padding: 0;
    margin: 20px 0px;
    border: 0;
    height: 0;
    border-top: 1px #ddd solid;
`;

export const PlaceholdersList = styled.ul`
    margin: 0;
    padding: 0;
    font-size: 12px;

    li {
        margin-bottom: 10px;

        ul {
            padding-top: 10px;
        }
    }

    span {
    }
`;

export interface FunctionaProfile {
    isAdvancedPricingEnabled: boolean;
    editPDFPreview: boolean;
    editSummary: boolean;
    integrations: boolean;
    bulkVariationForm: boolean;
    pricePerImage: boolean;
    engraving: boolean;
    editPrintfiles: boolean;
    printReadyFilesPlus: boolean;
    textplus: boolean;
    prices3dConfigurator: boolean;
    customEnvironmentTexture: boolean;
    dynamicProperties: boolean;
    materialLibrary: boolean;
    multiareaConfiguratorDesigner: boolean;
}

export interface OptionType {
    readonly value: string;
    readonly label: string;
    readonly subLabel: string;
}

const OptionContent = styled.div`
    display: block;

    strong {
        font-size: 14px;
        font-weight: 600;
    }

    p {
        margin: 0;
        margin-top: 5px;
        font-size: 12px;
    }
`;

export const OptionWithSubLabel = (props: OptionProps<OptionType, false>) => {
    return (
        <components.Option {...props}>
            <OptionContent>
                <strong>{props.data.label}</strong>
                <p>{props.data.subLabel}</p>
            </OptionContent>
        </components.Option>
    );
};

// interface _ButtonActionsProps {
// 	disabled?: boolean;
// 	selected?: boolean;
// }
//
// const _ButtonActions = styled.div<_ButtonActionsProps>`
// 	font-size: 12px;
// 	border-radius: 3px;
// 	width: 100%;
// 	display: flex;
// 	justify-content: flex-start;
// 	//align-items: flex-start;
// 	align-items: center;
// 	text-align: left;
// 	font-weight: 400;
// 	font-size: 14px;
// 	padding: 8px;
// 	color: ${(props) => props.theme.app.textColor} !important;
//
// 	${(props) =>
// 		props.selected &&
// 		`
//         color: ${props.theme.app.secondary};
//     `}
//
// 	${(props) => props.disabled && `opacity: ${props.theme.app.disabledOpacity};`}
//
// 	&:hover {
// 		background-color: #f6f8f9;
// 		cursor: pointer;
// 	}
//
// 	svg.Upgrade {
// 		margin-left: 10px;
// 		fill: #f0047f;
// 		width: 18px;
// 	}
// `;
//
// interface ButtonActionsProps {
// 	disabled?: boolean;
// 	selected?: boolean;
// 	requireUpgrade?: boolean;
// 	onClick: () => void;
// }
//
// export const ButtonActions: FC<PropsWithChildren<ButtonActionsProps>> = ({ disabled, selected, requireUpgrade, onClick, children }) => {
// 	return (
// 		<_ButtonActions selected={selected} disabled={disabled} onClick={() => !disabled && onClick && onClick()}>
// 			{children}
// 			{requireUpgrade && <Crown className='Upgrade'></Crown>}
// 		</_ButtonActions>
// 	);
// };

export const ButtonActionsDelete = styled.div`
    border-radius: 3px;
    width: 100%;
    display: flex;
    justify-content: flex-start;
    padding: 0.5rem;
    color: red;
    &:hover {
        background-color: #f6f8f9;
        cursor: pointer;
    }
`;

export const PlaceholderItem: FC<{ placeholder: TemplatePlaceholder }> = ({ placeholder }) => {
    return (
        <li>
            <strong>{`{{${placeholder.value}}}`}</strong>
            {placeholder.iterable && <BadgeGray>{T._('Iterable', 'Admin')}</BadgeGray>}
            {placeholder.complex && <BadgeGray>{T._('Complex', 'Admin')}</BadgeGray>}

            <br />

            <span>{placeholder.description}</span>
            {placeholder.children && (
                <ul>
                    {placeholder.children.map((x) => (
                        <PlaceholderItem key={x.value} placeholder={x} />
                    ))}
                </ul>
            )}
        </li>
    );
};

export { AiSparklesTag as AiSparkles, BaseCardTag, ComingSoonTag as ComingSoon, CrownBadgeTag as CrownBadge, EarlyAccessTag as EarlyAccess, NewTag as New };

const GradientTag = styled(BaseCardTag)`
    background: linear-gradient(135deg, #64d6ff 0%, #008be2 100%);
    color: #ffffff;
`;

const OnlyNewUIContainer = styled(BaseCardTag)`
    background: #dbeafe;
    color: #1e40af;
`;

const TOOL_TO_ENVIRONMENT: Record<number, ZakekeEnvironment> = {
    1: 'CUSTOMIZER',
    2: 'COMPOSER'
};

const OnlyNewUIQuery = graphql`
    query globalsOnlyNewUIQuery($environment: ZakekeEnvironment!) {
        sellerUserInterfaces(first: 1, environment: $environment) {
            nodes {
                isDefault
                userInterfaceId
            }
        }
    }
`;

const OnlyNewUIContent = ({ className }: { className?: string }) => {
    const { state } = useAppState();
    const environment = TOOL_TO_ENVIRONMENT[state.lastUsedTool] ?? 'CUSTOMIZER';
    const data = useLazyLoadQuery<globalsOnlyNewUIQuery>(OnlyNewUIQuery, { environment });
    const firstUserInterface = data.sellerUserInterfaces?.nodes?.at(0);

    if (firstUserInterface?.userInterfaceId !== 1) {
        return null;
    }

    return (
        <ZTooltip
            maxWidth={800}
            interactive={true}
            html={
                <div style={{ textAlign: 'left', minWidth: '400px', fontSize: '12px', letterSpacing: '0' }}>
                    {T._('🔒 Only available in the new Visual Product Customizer UI', 'Admin')}
                    <br />
                    <span
                        dangerouslySetInnerHTML={{
                            __html: T._(
                                "This feature is supported only in Zakeke's new interface. To access it, go to <strong>Settings > User Interface Settings > Continue with New UI</strong>",
                                'Admin'
                            )
                        }}
                    />
                    <br />
                    {T._("If you don't see the switch option, please contact us at ", 'Admin')}
                    <Link href='mailto:help@zakeke.com'>help@zakeke.com</Link>
                </div>
            }
        >
            <OnlyNewUIContainer className={className}>{T._('Only New UI', 'Admin')}</OnlyNewUIContainer>
        </ZTooltip>
    );
};

const _onlyNewUI = ({ className }: { className?: string }) => {
    return (
        <Suspense fallback={null}>
            <OnlyNewUIContent className={className} />
        </Suspense>
    );
};

export const OnlyNewUI = styled(_onlyNewUI)``;

const _onlyPaidPlans = ({ className }: { className?: string }) => {
    return (
        <ZTooltip position='top' title={T._('This add-on is not available during the free trial or in development stores on Shopify.', 'Admin')}>
            <GradientTag className={className}>{T._('Only Paid Plans', 'Admin')}</GradientTag>
        </ZTooltip>
    );
};

export const OnlyPaidPlans = styled(_onlyPaidPlans)``;

// export const PageTitle = styled.h2`
// 	font-size: ${(props) => props.theme.app.component.pageTitle.size};
// 	font-weight: ${(props) => props.theme.app.component.pageTitle.weight};
// 	font-family: ${(props) => props.theme.app.font};
// 	margin: 20px 0 20px 0 !important;
// 	color: ${(props) => props.theme.app.component.pageTitle.textColor};
// `;

export interface OptionValue {
    value: number;
    label: string;
}

export const ImportantNotesStyle = styled.div`
    width: 100%;
    box-shadow:
        0px 0px 2px rgba(0, 0, 0, 0.2),
        0px 2px 10px rgba(0, 0, 0, 0.1);
    border-radius: 4px;
    padding: 16px;
    background: #ffe1be;
    color: #a64f21;
    margin-top: 52px;
    h3,
    p {
        margin: 0;
        font-size: 14px;
    }
    p {
        margin-top: 20px;
        text-align: left;
    }
`;

interface ImportantNotesProps {
    title?: string;
    body?: string | React.ReactElement<any>;
}

export const ImportantNotes = ({ title, body }: ImportantNotesProps) => {
    return (
        <ImportantNotesStyle>
            <h3>{title}</h3>
            <p>{body}</p>
        </ImportantNotesStyle>
    );
};

// #region SlideShow
interface SlideShowProps {
    slides: React.ReactElement<any>[];
    showLeftRightArrows?: boolean;
    showTopNumbers?: boolean;
}
const FlexBlock = styled.div`
    display: flex;
    flex-direction: column;
    height: 100%;
`;
const SlideShowContainer = styled.div`
    position: relative;
    height: 100%;
`;
const Slide = styled.div`
    height: 100%;
    animation-name: fade;
    animation-duration: 1.5s;
`;
const DotContainer = styled.div`
    margin-top: 10px;
    text-align: center;
`;
const Dot = styled.span`
    cursor: pointer;
    height: 12px;
    width: 12px;
    margin: 0 2px;
    background-color: #98c22554;
    border-radius: 50%;
    display: inline-block;
    transition: background-color 0.6s ease;
`;
const SelectedDot = styled(Dot)`
    background-color: #98c225;
`;
export const SlideShow = ({ slides, showLeftRightArrows, showTopNumbers }: SlideShowProps) => {
    const [currentSlide, setCurrentSlide] = useState(slides.at(0));
    const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
    const setSlide = (slideIndex: number) => {
        if (slideIndex > slides.length - 1) {
            slideIndex = slides.length - 1;
        }
        if (slideIndex < 0) {
            slideIndex = 0;
        }
        setCurrentSlide(slides.at(slideIndex));
        setCurrentSlideIndex(slideIndex);
    };
    return (
        <FlexBlock>
            <SlideShowContainer>
                <Slide>{currentSlide}</Slide>
                {showLeftRightArrows && (
                    <>
                        <a onClick={() => setSlide(currentSlideIndex - 1)}>&#10094;</a>
                        <a onClick={() => setSlide(currentSlideIndex + 1)}>&#10095;</a>
                    </>
                )}
            </SlideShowContainer>
            <DotContainer>
                {slides.map((slide, index) => (
                    <>
                        {index == currentSlideIndex && <SelectedDot></SelectedDot>}
                        {index != currentSlideIndex && <Dot onClick={() => setSlide(index)}></Dot>}
                    </>
                ))}
            </DotContainer>
        </FlexBlock>
    );
};
// #endregion SlideShow

const PageLoadingContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100vh;
`;

export const PageLoading = () => {
    return (
        <PageLoadingContainer>
            <Loader />
        </PageLoadingContainer>
    );
};

//#InputError
const ErrorContainer = styled.div`
    display: inline-grid;
    grid-auto-flow: column;
    align-items: center;
    gap: 8px;
    margin-top: 4px;
`;

const ErrorInputMessage = styled.span`
    color: #9f1b1f;
    font-size: 12px;
    font-style: normal;
    font-weight: 400;
    line-height: 16px;
`;

const StyledInput = styled(Input)<{ $hasError: boolean }>`
    padding: 8px;
    border-radius: 4px;
    border: 1px solid ${({ $hasError }) => ($hasError ? '#DE1C22' : '#ddd')};
    background-color: ${({ $hasError }) => ($hasError ? '#FFF4F4' : '#fff')};
    outline: none;

    &:focus {
        padding: 8px;
        border-radius: 4px;
        border: 1px solid ${({ $hasError }) => ($hasError ? '#DE1C22' : '#ddd')};
        background-color: ${({ $hasError }) => ($hasError ? '#FFF4F4' : '#fff')};
        outline: none;
    }
`;

const PasswordInputError = styled(PasswordInput)<{ $hasError: boolean }>`
    padding: 8px;
    border-radius: 4px;
    border: 1px solid ${({ $hasError }) => ($hasError ? '#DE1C22' : '#ddd')};
    background-color: ${({ $hasError }) => ($hasError ? '#FFF4F4' : '#fff')};
    outline: none;

    &:focus {
        padding: 8px;
        border-radius: 4px;
        border: 1px solid ${({ $hasError }) => ($hasError ? '#DE1C22' : '#ddd')};
        background-color: ${({ $hasError }) => ($hasError ? '#FFF4F4' : '#fff')};
        outline: none;
    }
`;

interface InputErrorProps extends InputHTMLAttributes<HTMLInputElement> {
    error?: string;
    type?: 'text' | 'email' | 'password';
}
export const InputErrorMessage = ({ error }: InputErrorProps) => (
    <ErrorContainer>
        <ExclamationCircleSolid />
        <ErrorInputMessage>{error}</ErrorInputMessage>
    </ErrorContainer>
);

export const InputError = React.forwardRef<HTMLInputElement, InputErrorProps>(({ error, type, ...rest }, ref) => {
    return (
        <>
            {type === 'password' && <PasswordInputError {...rest} ref={ref} $hasError={!!error} />}
            {type !== 'password' && <StyledInput {...rest} ref={ref} $hasError={!!error} />}
            {error && <InputErrorMessage error={error} />}
        </>
    );
});
